import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8010';

/** Send/receive HttpOnly cookies (e.g. trusted_device after 2FA). */
const authHttp = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

const authAPI = {
  login: async (email, password, captcha = null, rememberMe = false) => {
    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);
    params.append('remember_me', rememberMe ? 'true' : 'false');
    
    // reCAPTCHA is required
    if (captcha && captcha.recaptcha_token) {
      params.append('recaptcha_token', captcha.recaptcha_token);
    } else {
      throw new Error('reCAPTCHA verification is required');
    }
    
    const response = await authHttp.post('/auth/login', params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
    return response.data;
  },

  register: async (userData, captcha = null) => {
    const data = { ...userData };
    
    // Add reCAPTCHA if provided
    if (captcha && captcha.recaptcha_token) {
      data.recaptcha_token = captcha.recaptcha_token;
    } else if (captcha) {
      // Fallback to simple CAPTCHA for backward compatibility
      data.captcha = {
        captcha_id: captcha.captcha_id,
        answer: captcha.answer
      };
    }
    
    const response = await axios.post(`${API_BASE_URL}/auth/register`, data);
    return response.data;
  },

  forgotPassword: async (email, recaptchaToken) => {
    const response = await axios.post(`${API_BASE_URL}/auth/forgot-password`, { 
      email,
      recaptcha_token: recaptchaToken 
    });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await axios.post(`${API_BASE_URL}/auth/reset-password`, {
      token,
      new_password: newPassword
    });
    return response.data;
  },

  getCurrentUser: async () => {
    const token = localStorage.getItem('authToken');
    const response = await axios.get(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  confirmEmail: async (token) => {
    const response = await axios.get(`${API_BASE_URL}/auth/confirm?token=${token}`);
    return response.data;
  },

  getGSTDetails: async (gstin) => {
    const response = await axios.get(`${API_BASE_URL}/auth/gst-details/${gstin}`);
    return response;
  },

  getIFSCDetails: async (ifsc) => {
    const response = await axios.get(`${API_BASE_URL}/auth/ifsc-details/${ifsc}`);
    return response;
  },

  /** Public: { available: boolean } — normalized IEC must be valid 10-char format */
  checkIecAvailable: async (iec) => {
    const response = await axios.get(
      `${API_BASE_URL}/auth/iec-available/${encodeURIComponent(iec)}`
    );
    return response.data;
  },

  verify2FALogin: async (preAuthToken, code, trustDevice = true, rememberMe = false) => {
    const response = await authHttp.post('/auth/2fa/verify-login', {
      pre_auth_token: preAuthToken,
      code,
      trust_device: trustDevice,
      remember_me: Boolean(rememberMe),
    });
    return response.data;
  },

  // Sub-user 2FA. Separate endpoints because a sub-user pending setup has no access
  // token at all — only a 2FA-scoped one — and sub-users get no trust-this-device option.
  subUserStart2FASetup: async (setupToken) => {
    const response = await authHttp.post('/auth/2fa/sub-user/setup', {
      setup_token: setupToken,
    });
    return response.data;
  },

  subUserVerify2FASetup: async (setupToken, code) => {
    const response = await authHttp.post('/auth/2fa/sub-user/verify-setup', {
      setup_token: setupToken,
      code,
    });
    return response.data;
  },

  subUserVerify2FALogin: async (preAuthToken, code, rememberMe = false) => {
    const response = await authHttp.post('/auth/2fa/sub-user/verify-login', {
      pre_auth_token: preAuthToken,
      code,
      remember_me: Boolean(rememberMe),
    });
    return response.data;
  },

  get2FAStatus: async () => {
    const token = localStorage.getItem('authToken');
    const response = await axios.get(`${API_BASE_URL}/auth/2fa/status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  setup2FA: async () => {
    const token = localStorage.getItem('authToken');
    const response = await axios.post(`${API_BASE_URL}/auth/2fa/setup`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  verify2FASetup: async (code) => {
    const token = localStorage.getItem('authToken');
    const response = await axios.post(`${API_BASE_URL}/auth/2fa/verify-setup`, { code }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  cancel2FASetup: async () => {
    const token = localStorage.getItem('authToken');
    const response = await axios.post(`${API_BASE_URL}/auth/2fa/cancel-setup`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
};

export default authAPI;
